import ApiService from '../apiService';
import { trackEvent } from '@/utils/tracking';

export default {
  // **********************************************************
  //
  // VIRTUAL DEVICES
  //
  // **********************************************************
  virtualDeviceFetch(
    context,
    userId,
    scopeIds,
    filters,
    options,
    orderBy,
    offset,
    limit,
    searchItems,
    restricted_query,
    includeIdentifiers,
    includeProperties,
    includeDrivers,
    searchOperator = 'AND',
    cancelOptions = {},
    ids_only
  ) {
    let cleanedScopeIds = scopeIds;
    if (cleanedScopeIds) {
      cleanedScopeIds.sort();
      cleanedScopeIds = cleanedScopeIds.map((sId) => parseInt(sId));
    }
    let scopeIdsJson = JSON.stringify(cleanedScopeIds);
    if (!scopeIdsJson) {
      scopeIdsJson = 'null';
    }
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    let filtersJson = JSON.stringify(filters);
    if (!filtersJson) {
      filtersJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    let orderByJson = JSON.stringify(orderBy);
    if (!orderByJson) {
      orderByJson = 'null';
    }
    let params = {
      user_id: userId,
      scope_ids: scopeIdsJson,
      offset,
      limit,
      search_items: searchJson,
      filters: filtersJson,
      order_by: orderByJson,
      options: optionsJson,
      restricted_query,
      search_operator: searchOperator,
      include_identifiers: includeIdentifiers,
      include_properties: includeProperties,
      include_drivers: includeDrivers,
      ids_only,
    };

    return ApiService.instance
      .get('/virtualdevice/', { params, timeout: 25000, cancelToken: cancelOptions.cancelToken })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceFetchGroups(context, accountIds) {
    let accountIdsJson = JSON.stringify(accountIds);
    if (!accountIdsJson) {
      accountIdsJson = 'null';
    }
    const params = {
      account_ids: accountIdsJson,
    };
    return ApiService.instance.get('/virtualdevice/groups', { params }).then((response) => {
      if (response.data && response.data.groupList) {
        response.data.groupList = response.data.groupList.map((g) => {
          return `${g}`; // MUST return a string to make string functions like toLowerCase working
        });
      }
      return response.data;
    });
  },

  virtualDeviceFetchTags() {
    return ApiService.instance.get('/virtualdevice/tags').then((response) => {
      return response.data;
    });
  },

  virtualDeviceCheckForGeoClusters() {
    return ApiService.instance.get('/virtualdevice/geo-cluster-check').then((response) => {
      return response.data;
    });
  },

  virtualDeviceFetchGeoMetaInfos(context, filters, searchItems, searchOperator = 'AND') {
    let filtersJson = JSON.stringify(filters);
    if (!filtersJson) {
      filtersJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    return ApiService.instance
      .get('/virtualdevice/geo-meta-infos/', {
        params: {
          search_items: searchJson,
          filters: filtersJson,
          search_operator: searchOperator,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceFetchGeoClusters(
    context,
    floorDtwinId,
    geoBounds,
    stateKey,
    filters,
    searchItems,
    parameters,
    searchOperator = 'AND'
  ) {
    let geoBoundsJson = JSON.stringify(geoBounds);
    if (!geoBoundsJson) {
      geoBoundsJson = 'null';
    }

    let filtersJson = JSON.stringify(filters);
    if (!filtersJson) {
      filtersJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    if (parameters.excludeZones === undefined) {
      parameters.excludeZones = false;
    }
    if (parameters.clusterDistance === undefined) {
      parameters.clusterDistance = 50;
    }
    if (parameters.hideStatelessPolygons === undefined) {
      parameters.hideStatelessPolygons = false;
    }

    return ApiService.instance
      .get('/virtualdevice/geo-clusters/', {
        params: {
          floor_dtwin_id: floorDtwinId,
          geo_bounds: geoBoundsJson,
          state_key: stateKey,
          search_items: searchJson,
          filters: filtersJson,
          parameters,
          search_operator: searchOperator,
        },
        timeout: 25000,
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceGetGeoClusterContent(context, clusterId, floorDtwinId, geoBounds, filters, searchItems) {
    let geoBoundsJson = JSON.stringify(geoBounds);
    if (!geoBoundsJson) {
      geoBoundsJson = 'null';
    }
    let filtersJson = JSON.stringify(filters);
    if (!filtersJson) {
      filtersJson = 'null';
    }
    let searchJson = JSON.stringify(searchItems);
    if (!searchJson) {
      searchJson = 'null';
    }
    return ApiService.instance
      .get(`/virtualdevice/geo-clusters/break-open/${clusterId}`, {
        params: {
          floor_dtwin_id: floorDtwinId,
          geo_bounds: geoBoundsJson,
          search_items: searchJson,
          filters: filtersJson,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceFetchGeoHistory(id, dateStart, dateEnd, offset, limit) {
    return ApiService.instance
      .get(`/virtualdevice/${id}/geo-history`, { params: { dateStart, dateEnd, offset, limit } })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceDeviceTypesParsePayloadN1(deviceTypeId, payload) {
    return ApiService.instance.post(`/virtualdevice/devicetype/${deviceTypeId}/parse`, payload).then((response) => {
      return response.data;
    });
  },

  virtualDeviceGet(context, id, bare) {
    return ApiService.instance
      .get(`/virtualdevice/${id}`, { params: { bare: bare ? true : false, no_descendents: true } })
      .then((response) => {
        return response.data;
      });
  },

  fetchVirtualDevicesByBridgeType(tenantId, bridgeType) {
    return ApiService.instance
      .get(`/virtualdevice/fetchByBridgeType/${tenantId}`, { params: { bridgeType } })
      .then((response) => {
        return response.data;
      });
  },

  async virtualDeviceCreate(context, data) {
    const response = await ApiService.instance.post('/virtualdevice/', data);
    trackEvent('virtualdevices', 'createOne');
    return response.data;
  },

  async virtualDeviceBulkUpdate(context, data) {
    const response = await ApiService.instance.put('/virtualdevice/bulk-update', data);
    trackEvent('virtualdevices', 'updateBulk');
    return response.data;
  },

  async virtualDeviceUpdate(context, data) {
    const response = await ApiService.instance.put(`/virtualdevice/${data.id}`, data);
    trackEvent('virtualdevices', 'updateOne');
    return response.data;
  },

  async virtualDeviceUpdateDisabled(context, id, disabled) {
    const response = await ApiService.instance.post(`/virtualdevice/${id}/disabled/${disabled}`);
    trackEvent('virtualdevices', disabled ? 'disableOne' : 'enableOne');
    return response.data;
  },

  async virtualDeviceDelete(id, deleteRemotely) {
    const response = await ApiService.instance.delete(`/virtualdevice/${id}?delete_remotely=${deleteRemotely}`, {
      timeout: 30000,
    });
    trackEvent('virtualdevices', 'deleteOne');
    return response.data;
  },

  virtualDeviceGetDependencies(id) {
    return ApiService.instance.get(`/virtualdevice/${id}/dependencies`).then((response) => {
      return response.data;
    });
  },

  async virtualDeviceSync(id, syncData) {
    const response = await ApiService.instance.post(`/virtualdevice/${id}/synchronize/`, syncData);
    trackEvent('virtualdevices', 'syncOne');
    return response.data;
  },

  virtualDeviceGetDeviceTypeHealthConfig(deviceTypeId) {
    return ApiService.instance.get(`/virtualdevice/health-config/device-type/${deviceTypeId}`).then((response) => {
      return response.data;
    });
  },

  virtualDeviceGetDeviceDriverHealthConfig(deviceDriverId) {
    return ApiService.instance.get(`/virtualdevice/health-config/device-driver/${deviceDriverId}`).then((response) => {
      return response.data;
    });
  },

  async virtualDeviceUpdateDeviceTypeHealthConfig(context, deviceTypeId, value, id = null) {
    const response = await ApiService.instance.post(`/virtualdevice/health-config/device-type/${deviceTypeId}`, {
      value,
      id,
    });
    trackEvent('virtualdevices', 'updateDeviceTypeHealthConfig');
    return response.data;
  },

  async virtualDeviceSyncHealthConfig(context, deviceId) {
    const response = await ApiService.instance.post(`/virtualdevice/${deviceId}/health-state-sync`);
    trackEvent('virtualdevices', 'syncHealthConfig');
    return response.data;
  },

  async virtualDeviceUpdateHealthConfig(context, deviceId, stateId, config) {
    const response = await ApiService.instance.post(
      `/virtualdevice/${deviceId}/health-config/update/${stateId}`,
      config
    );
    trackEvent('virtualdevices', 'updateHealthConfig');
    return response.data;
  },

  virtualDevicePacketStatistics(context, deviceId, startDate, stopDate, duration) {
    return ApiService.instance
      .get(`/virtualdevice/statistics/${deviceId}`, {
        params: {
          startDate,
          stopDate,
          duration,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceStatistics(context, options) {
    return ApiService.instance.post('/virtualdevice/statistics', options).then((response) => {
      return response.data;
    });
  },

  async virtualDeviceUnlinkFromTemplate(context, id) {
    const response = await ApiService.instance.post(`/virtualdevice/${id}/devicetemplate-unlink`);
    trackEvent('virtualdevices', 'unlinkFromTemplate');
    return response.data;
  },

  // **********************************************************
  //
  // MONITORING VIEWS
  //
  // **********************************************************

  async virtualDeviceViewCreate(context, options) {
    const response = await ApiService.instance.post('/virtualdevice/view', options);
    trackEvent('virtualdevices', 'createView');
    return response.data;
  },

  async virtualDeviceViewUpdate(context, options) {
    const response = await ApiService.instance.put(`/virtualdevice/view/${options.id}`, options);
    trackEvent('virtualdevices', 'updateView');
    return response.data;
  },

  async virtualDeviceViewDelete(context, viewId) {
    const response = await ApiService.instance.delete(`/virtualdevice/view/${viewId}`);
    trackEvent('virtualdevices', 'deleteView');
    return response.data;
  },

  virtualDeviceViewGet(context, viewId) {
    return ApiService.instance.get(`/virtualdevice/view/${viewId}`).then((response) => {
      return response.data;
    });
  },

  /** start and stop are optional, otherwise the fixed "today", "yesterday", "last 24hrs" will be returned */
  virtualDeviceViewGetPacketStatistics(context, viewId, operationalStatus, bridgeType, startDate, stopDate, duration) {
    return ApiService.instance
      .get(`/virtualdevice/view/${viewId}/packet-statistics`, {
        params: { operational_status: operationalStatus, bridge_type: bridgeType, startDate, stopDate, duration },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceViewGetDeviceStatistics(
    context,
    viewId,
    operationalStatus,
    bridgeType,
    startDate,
    stopDate,
    duration,
    countModes
  ) {
    return ApiService.instance
      .get(`/virtualdevice/view/${viewId}/device-statistics`, {
        params: {
          operational_status: operationalStatus,
          bridge_type: bridgeType,
          startDate,
          stopDate,
          duration,
          count_modes: countModes,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceViewsFetch(context, options) {
    return ApiService.instance
      .get('/virtualdevice/view/list', {
        params: {
          tenantIds: JSON.stringify(options.tenantIds),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  virtualDeviceViewsExecute(context, viewId, operationalStatus, bridgeType) {
    return ApiService.instance
      .get(`/virtualdevice/view/${viewId}/execute`, {
        params: { operational_status: operationalStatus, bridge_type: bridgeType },
      })
      .then((response) => {
        return response.data;
      });
  },

  // **********************************************************
  //
  // DEVICE TEMPLATES
  //
  // **********************************************************

  async deviceTemplateCreate(data) {
    const response = await ApiService.instance.post('/digitaltwin/device-templates', data);
    trackEvent('devicetemplates', 'createOne');
    return response.data;
  },

  async deviceTemplateCopy(id, data) {
    const response = await ApiService.instance.post(`/digitaltwin/device-templates/${id}/copy`, data);
    trackEvent('devicetemplates', 'copyOne');
    return response.data;
  },

  async deviceTemplateUpdate(data) {
    const response = await ApiService.instance.put(`/digitaltwin/device-templates/${data.id}`, data);
    trackEvent('devicetemplates', 'updateOne');
    return response.data;
  },

  async deviceTemplateSyncFromDriver(id, deviceDriverVersion) {
    const response = await ApiService.instance.post(`/digitaltwin/device-templates/${id}/sync-from-driver`, {
      deviceDriverVersion,
    });
    trackEvent('devicetemplates', 'syncFromDriver');
    return response.data;
  },

  deviceTemplatesFetch(opts) {
    const params = {
      pageSize: opts.pageSize,
      page: opts.page,
      sortBy: opts.sortBy,
      sortDesc: opts.sortDesc,
      search_operator: opts.searchOperator,
      tenantIds: opts.tenantIds,
    };
    // The backend parses searchItems from flat query keys (searchItems[<idx>][<field>]).
    // Passing the array directly lets default axios serialize it as searchItems[]={json},
    // which the backend regex never matches, so the filter is silently dropped.
    (opts.searchItems || []).forEach((item, idx) => {
      Object.keys(item).forEach((field) => {
        params[`searchItems[${idx}][${field}]`] = item[field];
      });
    });
    return ApiService.instance.get('/digitaltwin/device-templates', { params }).then((response) => {
      return response.data;
    });
  },

  async deviceTemplatePublishAndSync(data) {
    const response = await ApiService.instance.post(`/digitaltwin/device-templates/${data.id}/publish-and-sync`, data);
    trackEvent('devicetemplates', 'publishAndSyncOne');
    return response.data;
  },

  fetchSmartGroupRelatedTemplates(opts) {
    const params = {
      pageSize: opts.pageSize,
      page: opts.page,
      sortBy: opts.sortBy,
      sortDesc: opts.sortDesc,
      search: opts.search,
      smartGroupId: opts.smartGroupId,
    };
    return ApiService.instance
      .get('/digitaltwin/device-templates/fetch-smart-group-related-templates', { params })
      .then((response) => {
        return response.data;
      });
  },

  fetchLinkedVirtualDevices(opts) {
    const params = {
      pageSize: opts.pageSize,
      page: opts.page,
      tenantId: opts.tenantId,
    };
    return ApiService.instance
      .get(`/digitaltwin/device-templates/${opts.id}/fetch-linked-devices`, { params })
      .then((response) => {
        return response.data;
      });
  },

  deviceTemplateGetById(id) {
    return ApiService.instance.get(`/digitaltwin/device-templates/${id}`).then((response) => {
      return response.data;
    });
  },

  deviceTemplateGetByIdDraft(id) {
    return ApiService.instance.get(`/digitaltwin/device-templates/${id}/draft`).then((response) => {
      return response.data;
    });
  },

  deviceTemplateGetByIdProductive(id) {
    return ApiService.instance.get(`/digitaltwin/device-templates/${id}/productive`).then((response) => {
      return response.data;
    });
  },

  async deviceTemplateDelete(id) {
    const response = await ApiService.instance.delete(`/digitaltwin/device-templates/${id}`);
    trackEvent('devicetemplates', 'deleteOne');
    return response.data;
  },
};
